import { useQuery } from '@tanstack/react-query'
import { api } from './api'
import { isLoggedIn } from './auth'

export type EnabledApp = {
  id: number
  code: string
  name: string
  description: string
  icon: string
  status: 'PLANNED' | 'BETA' | 'ACTIVE' | 'DEPRECATED'
  is_enabled: boolean
  display_order: number
}

/**
 * Returns the set of module codes currently enabled in the App catalog.
 *
 * The /my-apps/ endpoint filters server-side on is_enabled=True so the result
 * is exactly the modules that should be visible & reachable for this session —
 * for BOTH end users and platform admins. Admins use the /admin/modules page
 * to flip is_enabled back on when they want a module to reappear.
 *
 * Shared cache key so AdminLayout, ProtectedRoute and UserPortal all read the
 * same query and updates from the Modules page propagate immediately.
 */
export function useEnabledApps() {
  const q = useQuery({
    queryKey: ['my-apps'],
    queryFn: async () => (await api.get<EnabledApp[]>('/my-apps/')).data,
    enabled: isLoggedIn(),
    staleTime: 60 * 1000,
    refetchOnWindowFocus: false,
  })

  const enabledCodes = new Set((q.data || []).map((a) => a.code))
  const isEnabled = (code: string) => enabledCodes.has(code)

  return {
    apps: q.data || [],
    enabledCodes,
    isEnabled,
    isLoading: q.isLoading,
    isError: q.isError,
  }
}
